import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Button from '../components/Button';
import Alert from '../components/Alert';
import { useClaimStore } from '../store/claimStore';
import { useEmbeddedApp } from '../providers/EmbeddedAppProvider';
import { useScreenNavigation } from '../hooks/useScreenNavigation';
import { getAssistanceEventsWithoutClaim } from '../services/api';
import { RequestType } from '../types';
import { Country, getCountryLabel } from '../types/Country.enum';
import { logger } from '../utils/logger';

const SCREEN_CODE = '00-00-SELECTION';

interface AssistanceEvent {
  eventId: string;
  eventNumber: string;
  contractNumber?: string;
  eventDate?: string;
  eventType?: string;
  beneficiaryName?: string;
  travelDepartureDate?: string;
  travelReturnDate?: string;
  destination?: string;
  destinationCity?: string;
  // Fields from ClaimDataResponse that are always present
  status: string;
  createdAt?: string;
  incidentDate?: string; // Actual date of the incident (from Incident_Date__c)
  flowType?: string;
}

const EventSelectionScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { parentContext } = useEmbeddedApp();
  useScreenNavigation(SCREEN_CODE);

  const [events, setEvents] = useState<AssistanceEvent[]>([]);
  const [selectedOption, setSelectedOption] = useState<'existing' | 'new' | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Format date to display only YYYY-MM-DD without time
   * Handles ISO 8601 format (2025-01-27T10:30:00.000Z) and date-only format (2025-01-27)
   */
  const formatDateOnly = (dateString: string | undefined): string => {
    if (!dateString) return '-';

    // Extract only the date part (YYYY-MM-DD)
    // Works for both ISO 8601 with time and date-only formats
    const datePart = dateString.split('T')[0];
    return datePart;
  };

  /**
   * Format destination by converting country code to full name and placing city first
   * Example: "USA, Wilmington" -> "Wilmington, États-Unis" (in French)
   * Example: "MEX, Oaxaca" -> "Oaxaca, Mexique" (in French)
   */
  const formatDestination = (destination: string | undefined): string => {
    if (!destination) return '-';

    // Check if destination contains a comma (country code, city format)
    const parts = destination.split(',').map(p => p.trim());
    if (parts.length >= 2) {
      const countryCode = parts[0];
      const city = parts.slice(1).join(', ');

      // Try to convert country code to full name
      try {
        const language = i18n.language === 'fr' ? 'fr' : 'en';
        const countryLabel = getCountryLabel(countryCode as Country, language);
        return `${city}, ${countryLabel}`; // City first, then country
      } catch (e) {
        // If country code not found, return as-is
        return destination;
      }
    }

    // If no comma, try to convert the whole string as a country code
    try {
      const language = i18n.language === 'fr' ? 'fr' : 'en';
      return getCountryLabel(destination as Country, language);
    } catch (e) {
      return destination;
    }
  };

  // Fetch assistance events on mount
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setIsLoading(true);
        const contractNumbers = parentContext?.contracts?.map(c => c.contractNumber) || [];

        const response = await getAssistanceEventsWithoutClaim(contractNumbers);
        // Map ClaimDataResponse to AssistanceEvent format
        const mappedEvents: AssistanceEvent[] = response.map((claim: any) => ({
          eventId: claim.eventId,
          eventNumber: claim.eventNumber,
          contractNumber: claim.contractNumber,
          eventDate: claim.incidentDate || claim.createdAt, // Use incident date, fallback to created date
          eventType: claim.flowType,
          status: claim.status,
          createdAt: claim.createdAt,
          incidentDate: claim.incidentDate,
          flowType: claim.flowType,
          // These fields now come directly from Event__c fields via ClaimDataResponse
          beneficiaryName: claim.beneficiaryFullName,
          travelDepartureDate: claim.travelDepartureDate,
          travelReturnDate: claim.travelReturnDate,
          destination: claim.destination, // Format: "MEX, Oaxaca" (will be formatted in UI)
          destinationCity: undefined, // Not needed - city is included in destination
        }));
        setEvents(mappedEvents);
      } catch (err) {
        logger.error('Error fetching events', {
          errorType: 'FetchEventsError',
          screen: 'EventSelectionScreen'
        }, err as Error);
        setError(t('errors.fetchEventsFailed'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, [parentContext, t]);

  const handleOptionChange = (option: 'existing' | 'new') => {
    setSelectedOption(option);
    if (option === 'new') {
      setSelectedEventId(null);
    }
  };

  const handleEventSelect = (eventId: string) => {
    setSelectedEventId(eventId);
    setSelectedOption('existing');
  };

  const handleContinue = () => {
    if (selectedOption === 'existing' && selectedEventId) {
      const selectedEvent = events.find(e => e.eventId === selectedEventId);
      if (!selectedEvent) {
        setError(t('errors.eventNotFound'));
        return;
      }

      if (!selectedEvent.contractNumber) {
        setError(t('errors.eventMissingContract'));
        return;
      }

      useClaimStore.getState().beginNewClaimOnExistingEvent(selectedEvent.eventNumber);
      navigate('/claim/00-02-AVIS');
    } else if (selectedOption === 'new') {
      // Log when user chooses new claim despite existing cases being available
      if (events.length > 0) {
        logger.warn('User bypassed existing cases to create new claim', {
          errorType: 'DuplicateRiskBypass',
          screen: 'EventSelectionScreen',
          existingCaseCount: events.length,
          existingEventNumbers: events.map(e => e.eventNumber).join(', '),
        });
      }
      // Continue normal flow (will create NEW Event + NEW Case)
      // IMPORTANT: Clear any previous claim data (including assistance case metadata)
      useClaimStore.getState().clearClaim();

      // Initialize a fresh new claim
      useClaimStore.getState().initializeClaim(RequestType.NEW_CLAIM, {
        type: RequestType.NEW_CLAIM
      });      navigate('/claim/00-02-AVIS');
    }
  };

  const canContinue = selectedOption !== null &&
    (selectedOption === 'new' || (selectedOption === 'existing' && selectedEventId !== null));

  return (
    <Layout
      title={t('eventSelection.title', 'Sélection de dossier')}
      showProgress={false}
    >
      <div className="max-w-4xl mx-auto">
        {/* Introduction text */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-gray-700">
            {t('eventSelection.intro',
              "Un ou plusieurs dossiers ont déjà été ouverts à votre nom ou pour l'une des personnes couvertes par votre contrat d'assurance, en lien avec un événement récent.")}
          </p>
        </div>

        <p className="mb-6 text-gray-700 font-medium">
          {t('eventSelection.instruction',
            'Sélectionnez le dossier pour lequel vous souhaitez soumettre une réclamation.')}
        </p>

        {/* Error alert */}
        {error && (
          <Alert
            type="error"
            message={error}
            onClose={() => setError(null)}
          />
        )}

        {/* Event cards */}
        {events.map((event) => (
          <div
            key={event.eventId}
            className={`mb-4 p-6 border-2 rounded-lg cursor-pointer transition-all ${
              selectedEventId === event.eventId
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onClick={() => handleEventSelect(event.eventId)}
          >
            <div className="flex items-start">
              <div className="mt-1 mr-4 flex-shrink-0">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedEventId === event.eventId
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-400'
                }`}>
                  {selectedEventId === event.eventId && (
                    <div className="w-2 h-2 rounded-full bg-white" />
                  )}
                </div>
                <input
                  type="radio"
                  name="event"
                  checked={selectedEventId === event.eventId}
                  onChange={() => handleEventSelect(event.eventId)}
                  className="sr-only"
                />
              </div>
              <div className="flex-1 grid grid-cols-2 gap-4">
                {/* Left column */}
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-600">
                      {t('eventSelection.eventNumber', 'Numéro de dossier')}
                    </span>
                    <p className="font-semibold">{event.eventNumber}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">
                      {t('eventSelection.eventDate', "Date de l'événement")}
                    </span>
                    <p className="font-semibold">{formatDateOnly(event.eventDate)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">
                      {t('eventSelection.beneficiary', 'Bénéficiaire')}
                    </span>
                    <p className="font-semibold">{event.beneficiaryName || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">
                      {t('eventSelection.travelDates', 'Dates du voyage')}
                    </span>
                    <p className="font-semibold">
                      {event.travelDepartureDate && event.travelReturnDate
                        ? `${formatDateOnly(event.travelDepartureDate)} au ${formatDateOnly(event.travelReturnDate)}`
                        : '-'}
                    </p>
                  </div>
                </div>

                {/* Right column */}
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-600">
                      {t('eventSelection.contractNumber', 'Numéro de contrat')}
                    </span>
                    <p className="font-semibold">{event.contractNumber || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">
                      {t('eventSelection.type', 'Type')}
                    </span>
                    <p className="font-semibold">
                      {event.eventType ? t(`eventSelection.types.${event.eventType.toLowerCase()}`, event.eventType) : '-'}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">
                      {t('eventSelection.destination', "Lieu de l'événement")}
                    </span>
                    <p className="font-semibold">
                      {formatDestination(event.destination)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Option for new event */}
        <div
          className={`mb-6 p-6 border-2 rounded-lg cursor-pointer transition-all ${
            selectedOption === 'new'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onClick={() => handleOptionChange('new')}
        >
          <div className="flex items-center">
            <input
              type="radio"
              name="event"
              checked={selectedOption === 'new'}
              onChange={() => handleOptionChange('new')}
              className="mr-4"
            />
            <p className="text-gray-700 font-medium">
              {t('eventSelection.otherEvent',
                'Ma réclamation est en lien avec un tout autre événement.')}
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
          >
            {t('common.previous', 'Précédent')}
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!canContinue || isLoading}
          >
            {t('common.continue', 'Continuer')}
          </Button>
        </div>
      </div>

    </Layout>
  );
};

export default EventSelectionScreen;
